import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { MetaProvider } from "@/context/MetaContext";
import { api } from "@/lib/api";
import AppLayout from "@/components/crm/AppLayout";
import Login from "@/pages/Login";
import Pending from "@/pages/Pending";
import Overview from "@/pages/Overview";
import LeadEngine from "@/pages/LeadEngine";
import Leads from "@/pages/Leads";
import LeadProfile from "@/pages/LeadProfile";
import Tasks from "@/pages/Tasks";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    api.post("/auth/session", { session_id: sid })
      .then((r) => { setUser(r.data); navigate("/", { replace: true }); })
      .catch(() => navigate("/login", { replace: true }));
  }, [location.hash, navigate, setUser]);
  return <FullLoader />;
}

const FullLoader = () => (
  <div className="min-h-screen grid place-items-center" data-testid="app-loader">
    <div className="h-6 w-6 rounded-full border-2 border-[#0B3C2D] border-t-transparent animate-spin" />
  </div>
);

function Protected() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.active) return <Pending />;
  return (
    <MetaProvider>
      <AppLayout>
        <Routes>
          <Route path="/" element={<Overview />} />
          <Route path="/lead-engine" element={<LeadEngine />} />
          <Route path="/leads" element={<Leads key="all" mode="all" />} />
          <Route path="/pipeline" element={<Leads key="pipeline" mode="pipeline" />} />
          <Route path="/customers" element={<Leads key="customers" mode="customers" />} />
          <Route path="/leads/:id" element={<LeadProfile />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppLayout>
    </MetaProvider>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/*" element={<Protected />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}
